import type { LabelSchema } from "@repo/validators";
import type { z } from "zod";
import { useEffect } from "react";
import { Permissions } from "@repo/database";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "~/lib/api";
import { Label } from "~/providers/label-provider";
import { useLabelStore } from "~/stores/use-label-store";
import { usePermissionManager } from "./use-permission-manager";

export const useLabelManager = () => {
  const { mutate: createLabel, isPending: isCreatingLabel } =
    api.label.createLabel.useMutation();

  const { mutate: updateLabel, isPending: isUpdatingLabel } =
    api.label.updateLabel.useMutation();

  const isLoading = isCreatingLabel || isUpdatingLabel;

  const { setLabelFormOpen, setSelectedLabel, setIsLoading } = useLabelStore();

  const { hasPermission } = usePermissionManager();

  const { t } = useTranslation("common");

  const createLabelHandler = async (
    label: z.infer<ReturnType<typeof LabelSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.create_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      createLabel(label, {
        onSuccess() {
          toast.success(t("create_item_success", { item: t("Labels.label") }));
          resolve(true);
        },
        onError() {
          toast.error(t("create_item_error", { item: t("Labels.label") }));
          resolve(false);
        },
      });
    });
  };

  const updateLabelHandler = async (
    id: string,
    vatRate: z.infer<ReturnType<typeof LabelSchema>>,
  ) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_vat_rate,
      label: t("Permissions.update_vat_rate_label"),
    });

    if (!permission) return false;

    return new Promise<boolean>((resolve) => {
      updateLabel(
        {
          id,
          ...vatRate,
        },
        {
          onSuccess() {
            toast.success(
              t("update_item_success", { item: t("Labels.label") }),
            );
            resolve(true);
          },
          onError() {
            toast.error(t("update_item_error", { item: t("Labels.label") }));
            resolve(false);
          },
        },
      );
    });
  };

  const openLabelForm = async (label?: Label) => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.create_vat_rate,
      label: t("Permissions.view_vat_rate_label"),
    });

    if (!permission) return false;

    setSelectedLabel(label);
    setLabelFormOpen(true);
  };

  const getLoadingState = () => {
    return isLoading;
  };

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  return {
    createLabel: createLabelHandler,
    updateLabel: updateLabelHandler,
    getLoadingState,
    openLabelForm,
  };
};
